import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@shared/ui/button";
import { PageHeader } from "@shared/ui/pageHeader";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import {
  ArrowLeft,
  MapPin,
  Edit,
  Plus,
  Download,
  Ban,
  Calendar,
  User,
  Repeat,
  Eye,
  Tag as TagIcon,
  CheckCircle,
} from "lucide-react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  useTerritoryMapping,
  useTerritoryPremiseStats,
  usePatchConfigMap,
  useExportTerritoryMapping,
} from "../../../hooks";
import { TerritoryMappingItem, EntityFilters } from "../../../types";
import { EntityFilterPanel } from "./EntityFilterPanel";
import { PAGE_SIZE } from "@shared/utils/constants";
import AddEntityModal from "../add/AddEntityModal";
import { TimelineTab } from "@features/cx/shared/components/tabs/TimelineTab";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Tabs } from "@shared/ui/tabs";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { HierarchyCard } from "./components/HierarchyCard";

interface EntityDetailViewProps {
  result?: any;
  entityData?: any;
  onBack?: () => void;
}

interface HierarchyConfig {
  typeDisplay: string;
  parentType: string;
  parentKey: string;
  childType: string;
  childLabelPlural: string;
  addChildButtonText: string;
  childEntityTypeVal: string;
}

const HIERARCHY_CONFIG: Record<string, HierarchyConfig> = {
  region: {
    typeDisplay: "Region",
    parentType: "",
    parentKey: "",
    childType: "Country",
    childLabelPlural: "Countries",
    addChildButtonText: "Add Country",
    childEntityTypeVal: "country",
  },
  country: {
    typeDisplay: "Country",
    parentType: "Region",
    parentKey: "region",
    childType: "State",
    childLabelPlural: "States",
    addChildButtonText: "Add State",
    childEntityTypeVal: "state",
  },
  state: {
    typeDisplay: "State",
    parentType: "Country",
    parentKey: "country",
    childType: "City/County",
    childLabelPlural: "City/Counties",
    addChildButtonText: "Add City/County",
    childEntityTypeVal: "city_county",
  },
  city_county: {
    typeDisplay: "City/County",
    parentType: "State",
    parentKey: "state",
    childType: "Zone",
    childLabelPlural: "Zones",
    addChildButtonText: "Add Zone",
    childEntityTypeVal: "zone",
  },
  zone: {
    typeDisplay: "Zone",
    parentType: "City/County",
    parentKey: "city_county",
    childType: "Division",
    childLabelPlural: "Divisions",
    addChildButtonText: "Add Division",
    childEntityTypeVal: "division",
  },
  division: {
    typeDisplay: "Division",
    parentType: "Zone",
    parentKey: "zone",
    childType: "Area",
    childLabelPlural: "Areas",
    addChildButtonText: "Add Area",
    childEntityTypeVal: "area",
  },
  area: {
    typeDisplay: "Area",
    parentType: "Division",
    parentKey: "division",
    childType: "Sub-Area",
    childLabelPlural: "Sub-Areas",
    addChildButtonText: "Add Sub-Area",
    childEntityTypeVal: "sub_area",
  },
  sub_area: {
    typeDisplay: "Sub-Area",
    parentType: "Area",
    parentKey: "area",
    childType: "Premise",
    childLabelPlural: "Premises",
    addChildButtonText: "Add Premise",
    childEntityTypeVal: "premise",
  },
  premise: {
    typeDisplay: "Premise",
    parentType: "Sub-Area",
    parentKey: "sub_area",
    childType: "Unit",
    childLabelPlural: "Units",
    addChildButtonText: "Add Unit",
    childEntityTypeVal: "unit",
  },
};

const EntityDetailView: React.FC<EntityDetailViewProps> = ({
  result,
  entityData: propEntityData,
  onBack,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();
  const itemsPerPage = PAGE_SIZE;
  const { code } = useParams<{ code: string }>();
  const decodedCode = decodeURIComponent(code || "");
  const tab = decodeURIComponent(searchParams.get("tab") || "");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddChildModalOpen, setIsAddChildModalOpen] = useState(false);

  // Get entity data from multiple sources with proper fallbacks
  const { data: entityData } = location.state || {};
  const resultEntityData = result;
  const initialEntity = propEntityData || entityData || resultEntityData || {};

  const [entityCode, setEntityCode] = useState<string>(() => {
    return initialEntity?.code || decodedCode || "";
  });

  useEffect(() => {
    const newCode = propEntityData?.code || entityData?.code || result?.code;
    if (newCode && newCode !== entityCode) {
      setEntityCode(newCode);
    }
  }, [propEntityData?.code, entityData?.code, result?.code, entityCode]);

  useEffect(() => {
    if (decodedCode && decodedCode !== entityCode) {
      setEntityCode(decodedCode);
    }
  }, [decodedCode, entityCode]);

  const getFiltersFromUrl = useCallback((): EntityFilters => {
    const page = parseInt(searchParams.get("page") || "1");
    const urlFilters: EntityFilters = {
      page,
      limit: itemsPerPage,
      territory_code: entityCode,
    };

    const territoryTypeParam = searchParams.get("territoryType");
    if (territoryTypeParam) urlFilters.territoryType = territoryTypeParam;

    const statusParam = searchParams.get("status");
    if (statusParam) urlFilters.status = statusParam === "true";

    const createdOnParam = searchParams.get("createdOn");
    if (createdOnParam) urlFilters.createdOn = createdOnParam;

    const queryParam = searchParams.get("query");
    if (queryParam) urlFilters.search_data = queryParam;

    return urlFilters;
  }, [searchParams, entityCode, itemsPerPage]);

  const [filters, setFilters] = useState<EntityFilters>(getFiltersFromUrl);
  const [entity, setEntity] = useState(initialEntity);

  useEffect(() => {
    const newFilters = getFiltersFromUrl();
    setFilters(newFilters);
  }, [getFiltersFromUrl]);

  const { data: territoryData } = useTerritoryMapping(
    filters.page || 1,
    filters.limit || itemsPerPage,
    entityCode,
    filters.search_data,
    "1",
    filters,
  );

  const { data: statsData } = useTerritoryPremiseStats(entityCode);

  // Determine entity type key
  const rawType = (
    statsData?.basicInfo?.entityTypeDisplay ||
    entity?.territoryType ||
    tab ||
    "region"
  )
    .toLowerCase()
    .trim()
    .replace(/[\/\s]/g, "_");

  const currentTypeKey =
    rawType === "premises"
      ? "premise"
      : rawType === "county" || rawType === "city"
        ? "city_county"
        : rawType === "areas"
          ? "area"
          : rawType === "sub_areas" || rawType === "sub-areas"
            ? "sub_area"
            : rawType;

  const currentConfig =
    HIERARCHY_CONFIG[currentTypeKey] || HIERARCHY_CONFIG.region;

  const patchConfigMapMutation = usePatchConfigMap();

  const handleToggleStatus = () => {
    patchConfigMapMutation.mutate({
      mapType: "TERRITORY_JSON",
      code: entityCode,
      is_active: !isActive,
      remote_utility_id: remoteUtilityId,
    });
  };

  const exportTerritoryMappingMutation = useExportTerritoryMapping();

  const handleExportData = async () => {
    try {
      const blob = await exportTerritoryMappingMutation.mutateAsync({
        remoteUtilityId: remoteUtilityId || "",
        page: filters.page || 1,
        limit: filters.limit || itemsPerPage,
        territoryCode: entityCode,
        searchData: filters.search_data,
        isPaginationRequired: "1",
        filters,
      });

      // Handle the file download
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `territory-mapping-${entityCode || "export"}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed", error);
    }
  };

  const entityName =
    statsData?.basicInfo?.name ||
    entity?.name ||
    `${currentConfig.typeDisplay} 1`;
  const configurationCode = statsData?.basicInfo?.configurationCode || "N/A";
  const isActive =
    statsData?.basicInfo?.isActive !== undefined
      ? statsData.basicInfo.isActive
      : entity?.isActive !== false;
  const createdDate =
    statsData?.basicInfo?.createdDate || entity?.createdDate || "N/A";
  const createdBy =
    statsData?.basicInfo?.createdBy || entity?.createdBy || "N/A";
  const updatedDate =
    statsData?.basicInfo?.updatedDate || entity?.updatedDate || "N/A";
  const updatedBy =
    statsData?.basicInfo?.updatedBy || entity?.updatedBy || "N/A";
  const description =
    (statsData?.basicInfo as any)?.description ||
    (entity as any)?.description ||
    "N/A";
  const parentName =
    entity?.hierarchyNames?.[currentConfig.parentKey] ||
    entity?.parentName ||
    (currentConfig.parentType ? `${currentConfig.parentType} 1` : "");

  const handleBackClick = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(`/utility/utility-setup/service-area?tab=${tab || "dashboard"}`);
    }
  };

  const handleSearch = (searchTerm: string, paramName: string) => {
    const params = new URLSearchParams(searchParams);
    if (searchTerm.trim()) {
      params.set(paramName, searchTerm);
    } else {
      params.delete(paramName);
    }
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  const handleApplyFilters = (newFilters: Partial<EntityFilters>) => {
    const params = new URLSearchParams(searchParams);
    params.delete("territoryType");
    params.delete("status");
    params.delete("createdOn");
    params.delete("page");

    if (newFilters.territoryType)
      params.set("territoryType", newFilters.territoryType);
    if (newFilters.status !== undefined)
      params.set("status", newFilters.status.toString());
    if (newFilters.createdOn) params.set("createdOn", newFilters.createdOn);

    params.set("page", "1");
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("territoryType");
    params.delete("status");
    params.delete("createdOn");
    params.delete("page");
    params.delete("query");
    setSearchParams(params);
  };

  const displayChildData = useMemo(() => {
    if (!territoryData) return [];
    if (Array.isArray(territoryData)) return territoryData;
    if (Array.isArray(territoryData.result)) return territoryData.result;
    if (Array.isArray((territoryData as any).result?.result))
      return (territoryData as any).result.result;
    return [];
  }, [territoryData]);

  const totalChildCount = useMemo(() => {
    if (!territoryData) return 0;
    if (typeof territoryData.count === "number") return territoryData.count;
    if (typeof (territoryData as any).count === "string")
      return parseInt((territoryData as any).count) || 0;
    if (typeof (territoryData as any).result?.count === "number")
      return (territoryData as any).result.count;
    return displayChildData.length;
  }, [territoryData, displayChildData]);

  const childColumns: TableColumn<any>[] = [
    {
      key: "name",
      header: "Name",
      type: "custom",
      render: (_: any, row: any) => (
        <span className="font-bold text-gray-900">{row.name || "-"}</span>
      ),
    },
    {
      key: "configurationCode",
      header: "Code",
      type: "custom",
      render: (_: any, row: any) => (
        <span className="text-xs font-mono bg-gray-100 border border-gray-200 px-2 py-0.5 rounded text-gray-800 font-medium">
          {row.configurationCode || row.code || "-"}
        </span>
      ),
    },
    {
      key: "type",
      header: "Type",
      type: "custom",
      render: (_: any, row: any) => {
        const displayVal = row.territoryTypeDisplay || "N/A";

        return (
          <span className="text-xs font-medium px-2.5 py-0.5 rounded border bg-gray-50 border-gray-200 text-gray-700">
            {displayVal}
          </span>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      type: "custom",
      render: (_: any, row: any) => {
        const active = row.isActive !== false;
        const statusText = active ? "Active" : "Inactive";
        return (
          <span
            className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${getStatusBadgeClass(
              statusText,
            )}`}
          >
            {statusText}
          </span>
        );
      },
    },
    {
      key: "meters",
      header: "Meters",
      type: "custom",
      render: (_: any, row: any) => (
        <span className="text-sm text-gray-800">
          {row.meterCount ?? row.meters ?? 0}
        </span>
      ),
    },
    {
      key: "consumers",
      header: "Consumers",
      type: "custom",
      render: (_: any, row: any) => (
        <span className="text-sm text-gray-800">
          {Number(row.consumerCount ?? row.consumers ?? 0).toLocaleString()}
        </span>
      ),
    },
  ];

  const action: TableAction<any>[] = [
    {
      label: "View",
      icon: <Eye className="w-3.5 h-3.5 mr-1 text-gray-500" />,
      onClick: (row: any) => {
        const isUnit = currentConfig.childEntityTypeVal === "unit";
        const basePath = isUnit
          ? `/utility/utility-setup/service-area/unit/view`
          : `/utility/utility-setup/service-area/view`;
        navigate(
          `${basePath}/${encodeURIComponent(
            row.code || row.name,
          )}?tab=${currentConfig.childEntityTypeVal}`,
        );
      },
    },
  ];

  const currentPage = parseInt(searchParams.get("page") || "1");

  // Hierarchy list path build
  const hierarchyPath = [
    { label: "Region", key: "region" },
    { label: "Country", key: "country" },
    { label: "State", key: "state" },
    { label: "City/County", key: "city_county" },
    { label: "Zone", key: "zone" },
    { label: "Division", key: "division" },
    { label: "Area", key: "area" },
    { label: "Sub-Area", key: "sub_area" },
    { label: "Premise", key: "premise" },
  ];

  const currentHierarchyIndex = hierarchyPath.findIndex(
    (item) => item.key === currentTypeKey,
  );

  const detailFields = useMemo(
    () => [
      {
        label: "Entity Type",
        value: (
          <span className="font-medium inline-flex items-center gap-1">
            {currentConfig.typeDisplay}
          </span>
        ),
      },
      {
        label: "Name",
        value: (
          <span className="font-medium inline-flex items-center gap-1">
            {entityName}
          </span>
        ),
      },
      {
        label: "Code",
        value: (
          <span className="font-mono text-xs bg-gray-100 border border-gray-200 text-gray-800 px-2 py-0.5 rounded font-medium">
            {configurationCode}
          </span>
        ),
      },
      {
        label: "Status",
        value: (
          <span
            className={`text-xs font-semibold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1 ${getStatusBadgeClass(
              isActive ? "Active" : "Inactive",
            )}`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                isActive ? "bg-emerald-500" : "bg-red-500"
              }`}
            />
            {isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        label: "Created On",
        value: createdDate,
      },
      {
        label: "Created By",
        value: createdBy,
      },
      {
        label: "Updated On",
        value: updatedDate,
      },
      {
        label: "Updated By",
        value: updatedBy,
      },
    ],
    [
      currentConfig.typeDisplay,
      entityName,
      configurationCode,
      isActive,
      createdDate,
      createdBy,
    ],
  );

  return (
    <div className="space-y-6 p-2">
      <PageHeader
        title="Service Area Details"
        description={`View detailed information and hierarchy for ${entityName}`}
        onBack={onBack}
      />

      {/* Entity Banner & Quick Actions Section */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Main Info Card (3 Cols) */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200/80 p-5 shadow-xs space-y-5">
          {/* Header info row */}
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0 mt-0.5">
              <MapPin className="w-5 h-5" />
            </div>
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                  {entityName}
                </h2>
                <span className="font-mono text-xs text-gray-700 bg-gray-100 border border-gray-200 px-2.5 py-0.5 rounded font-medium">
                  {configurationCode}
                </span>
                <span
                  className={`text-xs font-semibold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1.5 ${getStatusBadgeClass(
                    isActive ? "Active" : "Inactive",
                  )}`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      isActive ? "bg-emerald-500" : "bg-red-500"
                    }`}
                  />
                  {isActive ? "Active" : "Inactive"}
                </span>
                <span className="text-xs text-gray-600 bg-gray-50 border border-gray-200 px-2.5 py-0.5 rounded font-medium">
                  {currentConfig.typeDisplay}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 pt-0.5">
                <span className="inline-flex items-center gap-1">
                  <User className="w-3.5 h-3.5 text-gray-400" />
                  {createdBy}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-gray-400" />
                  Created {createdDate}
                </span>
                {currentConfig.parentType && parentName && (
                  <span className="inline-flex items-center gap-1 text-blue-600 font-medium">
                    <Repeat className="w-3.5 h-3.5 text-blue-500" />
                    Under {parentName}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Key-Value Summary Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-y-4 gap-x-6 pt-3 border-t border-gray-100 text-xs">
            {detailFields.map((field, idx) => (
              <ValueLabel key={idx} label={field.label} value={field.value} />
            ))}
          </div>
          <div className="pt-3 text-xs">
            <ValueLabel
              label="Description"
              value={<span className="text-gray-700">{description}</span>}
            />
          </div>
        </div>

        {/* Quick Actions Card (1 Col) */}
        <div className="bg-white rounded-xl border border-gray-200/80 p-5 shadow-xs flex flex-col space-y-3">
          <h3 className="text-sm font-bold text-gray-800 tracking-wide">
            Quick Actions
          </h3>
          <div className="space-y-2">
            <Button
              variant="outline"
              onClick={() => setIsEditModalOpen(true)}
              className="w-full justify-start"
            >
              <Edit className="w-3.5 h-3.5 mr-2 text-gray-500" />
              Edit
            </Button>

            {currentConfig.childType && (
              <Button
                onClick={() => setIsAddChildModalOpen(true)}
                className="w-full justify-start"
              >
                <Plus className="w-3.5 h-3.5 mr-2" />
                {currentConfig.addChildButtonText}
              </Button>
            )}

            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={handleExportData}
              isLoading={exportTerritoryMappingMutation.isPending}
            >
              <Download className="w-3.5 h-3.5 mr-2 text-gray-500" />
              Export Data
            </Button>

            <Button
              variant="outline"
              onClick={handleToggleStatus}
              isLoading={patchConfigMapMutation.isPending}
              className="w-full justify-start cursor-pointer"
            >
              <Ban className="w-3.5 h-3.5 mr-2 text-orange-500" />
              {isActive ? "Mark Inactive" : "Mark Active"}
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        defaultValue="details"
        className="w-full"
        tabsListClassName="grid grid-cols-2 w-full max-w-sm"
        urlMapping={{
          details: "details",
          activityLog: "activity-log",
        }}
        tabComponents={{
          details: {
            label: "Details",
            component: (
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 pt-4">
                {/* Main Left Column (Child Table) */}
                <div className="lg:col-span-3 space-y-4">
                  {currentConfig.childType ? (
                    <div className="bg-white rounded-xl border border-gray-200/80 p-5 shadow-xs space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                          <User className="w-4 h-4 text-gray-400" />
                          <span>
                            {currentConfig.childLabelPlural} · {totalChildCount}{" "}
                            shown
                          </span>
                        </h3>
                      </div>

                      <GenericTable
                        data={displayChildData || []}
                        columns={childColumns}
                        searchPlaceholder={`Search ${currentConfig.childType.toLowerCase()}...`}
                        searchParamName="query"
                        searchDebounceMs={300}
                        onSearch={handleSearch}
                        actions={action}
                        actionsType="icons"
                        rowKey={(row: any) =>
                          row.code ||
                          row.configurationCode ||
                          row.configuration_code ||
                          row.name
                        }
                        pagination={{
                          currentPage: currentPage,
                          totalPages: Math.ceil(totalChildCount / itemsPerPage),
                          totalItems: totalChildCount,
                          itemsPerPage,
                          startItem: (currentPage - 1) * itemsPerPage + 1,
                          endItem: Math.min(
                            currentPage * itemsPerPage,
                            totalChildCount,
                          ),
                        }}
                        onPageChange={handlePageChange}
                        filterPanel={
                          <EntityFilterPanel
                            currentFilters={filters}
                            onApplyFilters={handleApplyFilters}
                            onResetFilters={handleResetFilters}
                          />
                        }
                        emptyMessage={`No ${currentConfig.childLabelPlural} found`}
                      />
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl border border-gray-200/80 p-8 text-center text-gray-500 space-y-2">
                      <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto" />
                      <h4 className="font-semibold text-gray-800 text-base">
                        Final Hierarchy Level
                      </h4>
                      <p className="text-xs text-gray-500">
                        {entityName} is at the final leaf level of the territory
                        hierarchy.
                      </p>
                    </div>
                  )}
                </div>

                {/* Right Column: Hierarchy */}
                <div className="space-y-4">
                  <HierarchyCard
                    currentLevelKey={currentTypeKey}
                    currentEntityName={entityName}
                    childData={displayChildData}
                    childConfig={currentConfig}
                    onChildClick={(childItem: any) =>
                      navigate(
                        `/utility/utility-setup/service-area/view/${encodeURIComponent(
                          childItem.code || childItem.name,
                        )}?tab=${currentConfig.childEntityTypeVal}`,
                      )
                    }
                  />
                </div>
              </div>
            ),
          },
          activityLog: {
            label: "Activity Log",
            component: (
              <div className="pt-4">
                <div className="bg-white rounded-xl border border-gray-200/80 p-5 shadow-xs">
                  <TimelineTab
                    remoteUtilityId={Number(remoteUtilityId) || 1}
                    keyCode={entityCode || "1"}
                    limit={10}
                  />
                </div>
              </div>
            ),
          },
        }}
        level={0}
      />

      {/* Edit Modal */}
      {isEditModalOpen && (
        <AddEntityModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          editData={statsData || entity}
          editMode={true}
          entityType={currentTypeKey}
        />
      )}

      {/* Add Child Modal */}
      {isAddChildModalOpen && (
        <AddEntityModal
          isOpen={isAddChildModalOpen}
          onClose={() => setIsAddChildModalOpen(false)}
          entityType={currentConfig.childEntityTypeVal}
        />
      )}
    </div>
  );
};

export default EntityDetailView;
